import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  ListItem,
  Collapse,
  IconButton,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import AddIcon from "@mui/icons-material/Add";
import { useUpdateCategory, useSubcategories, useUpdateSubcategory, useDeleteSubcategory, useCreateSubcategory } from "../../hooks/useCategories";
import type { CategoryResponse, SubcategoryResponse } from "../../api/generated";

interface CategoryListItemProps {
  category: CategoryResponse;
  onEdit: (category: CategoryResponse) => void;
  onDelete: (category: CategoryResponse) => void;
}

interface SubcategoryItemProps {
  subcategory: SubcategoryResponse;
  onDelete: (subcategory: SubcategoryResponse) => void;
}

const SubcategoryItem: React.FC<SubcategoryItemProps> = ({ subcategory, onDelete }) => {
  const updateSubcategory = useUpdateSubcategory();
  const [name, setName] = useState(subcategory.name || "");
  const [description, setDescription] = useState(subcategory.description || "");
  const [editingName, setEditingName] = useState(false);
  const [editingDescription, setEditingDescription] = useState(false);

  useEffect(() => {
    setName(subcategory.name || "");
    setDescription(subcategory.description || "");
  }, [subcategory]);

  const hasUnsavedChanges = name !== (subcategory.name || "") || description !== (subcategory.description || "");

  const handleNameBlur = () => setEditingName(false);
  const handleNameKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.currentTarget.blur();
    } else if (e.key === "Escape") {
      setName(subcategory.name || "");
      setEditingName(false);
    }
  };

  const handleDescriptionBlur = () => setEditingDescription(false);
  const handleDescriptionKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.currentTarget.blur();
    } else if (e.key === "Escape") {
      setDescription(subcategory.description || "");
      setEditingDescription(false);
    }
  };

  const handleSave = async () => {
    const updates: { name?: string | null; description?: string | null } = {};
    if (name !== (subcategory.name || "")) {
      updates.name = name || null;
    }
    if (description !== (subcategory.description || "")) {
      updates.description = description || null;
    }
    if (Object.keys(updates).length > 0) {
      await updateSubcategory.mutateAsync({
        subcategoryId: subcategory.id,
        data: updates,
      });
      setEditingName(false);
      setEditingDescription(false);
    }
  };

  return (
      <ListItem
        sx={{
          pl: 6,
          py: 0.5,
          px: 0,
          "&:hover": {
            backgroundColor: "transparent",
          },
        }}
      >
      <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 0.5, py: 0.5 }}>
        {/* Name */}
        {editingName ? (
          <TextField
            value={name}
            onChange={(e) => setName(e.target.value)}
            onBlur={handleNameBlur}
            onKeyDown={handleNameKeyDown}
            placeholder="Subcategory name"
            size="small"
            fullWidth
            autoFocus
            disabled={updateSubcategory.isPending}
            variant="standard"
            sx={{
              "& .MuiInputBase-input": {
                fontSize: { xs: "0.75rem", sm: "0.875rem" },
                py: 0.25,
              },
              "& .MuiInput-underline:before": {
                borderBottom: "none",
              },
              "& .MuiInput-underline:hover:before": {
                borderBottom: "none",
              },
              "& .MuiInput-underline:after": {
                borderBottom: "none",
              },
            }}
          />
        ) : (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              cursor: "pointer",
              py: 0.25,
              "&:hover": {
                opacity: 0.7,
              },
            }}
            onClick={() => setEditingName(true)}
          >
            <Typography
              variant="body2"
              sx={{
                fontSize: { xs: "0.75rem", sm: "0.875rem" },
                color: name ? "text.primary" : "text.secondary",
                fontStyle: name ? "normal" : "italic",
                fontWeight: 500,
              }}
            >
              {name || "add name"}
            </Typography>
            <EditIcon sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" }, color: "text.secondary" }} />
          </Box>
        )}

        {/* Description */}
        {editingDescription ? (
          <TextField
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            onBlur={handleDescriptionBlur}
            onKeyDown={handleDescriptionKeyDown}
            placeholder="Description"
            size="small"
            fullWidth
            autoFocus
            disabled={updateSubcategory.isPending}
            variant="standard"
            sx={{
              "& .MuiInputBase-input": {
                fontSize: { xs: "0.75rem", sm: "0.875rem" },
                py: 0.25,
              },
              "& .MuiInput-underline:before": {
                borderBottom: "none",
              },
              "& .MuiInput-underline:hover:before": {
                borderBottom: "none",
              },
              "& .MuiInput-underline:after": {
                borderBottom: "none",
              },
            }}
          />
        ) : (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              cursor: "pointer",
              py: 0.25,
              "&:hover": {
                opacity: 0.7,
              },
            }}
            onClick={() => setEditingDescription(true)}
          >
            <Typography
              variant="body2"
              sx={{
                fontSize: { xs: "0.75rem", sm: "0.875rem" },
                color: description ? "text.primary" : "text.secondary",
                fontStyle: description ? "normal" : "italic",
              }}
            >
              {description || "add description"}
            </Typography>
            <EditIcon sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" }, color: "text.secondary" }} />
          </Box>
        )}

        {/* Actions */}
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 0.5, pt: 0.25 }}>
          <Button
            variant="text"
            startIcon={<SaveIcon />}
            disableRipple
            size="small"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleSave();
            }}
            disabled={!hasUnsavedChanges || updateSubcategory.isPending}
            sx={{
              textTransform: "none",
              color: "primary.main",
              px: 0.5,
              minWidth: "auto",
              fontSize: { xs: "0.65rem", sm: "0.75rem" },
              "& .MuiButton-startIcon": {
                marginRight: 0.25,
              },
            }}
          >
            Save
          </Button>
          <IconButton
            size="small"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onDelete(subcategory);
            }}
            sx={{
              color: "error.main",
              padding: 0.5,
            }}
          >
            <DeleteIcon sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }} />
          </IconButton>
        </Box>
      </Box>
    </ListItem>
  );
};

export const CategoryListItem: React.FC<CategoryListItemProps> = ({
  category,
  onEdit,
  onDelete,
}) => {
  const updateCategory = useUpdateCategory();
  const { data: subcategoriesData } = useSubcategories({ category_id: category.id });
  const subcategories = subcategoriesData?.items || [];
  const [expanded, setExpanded] = useState(false);
  const [name, setName] = useState(category.name || "");
  const [description, setDescription] = useState(category.description || "");
  const [editingName, setEditingName] = useState(false);
  const [editingDescription, setEditingDescription] = useState(false);
  const [newSubcategoryName, setNewSubcategoryName] = useState("");
  const [newSubcategoryDescription, setNewSubcategoryDescription] = useState("");
  const [addingSubcategory, setAddingSubcategory] = useState(false);

  const createSubcategory = useCreateSubcategory();
  const deleteSubcategory = useDeleteSubcategory();

  useEffect(() => {
    setName(category.name || "");
    setDescription(category.description || "");
  }, [category]);

  const hasUnsavedChanges = name !== (category.name || "") || description !== (category.description || "");

  const handleNameBlur = () => setEditingName(false);
  const handleNameKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.currentTarget.blur();
    } else if (e.key === "Escape") {
      setName(category.name || "");
      setEditingName(false);
    }
  };

  const handleDescriptionBlur = () => setEditingDescription(false);
  const handleDescriptionKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.currentTarget.blur();
    } else if (e.key === "Escape") {
      setDescription(category.description || "");
      setEditingDescription(false);
    }
  };

  const handleSave = async () => {
    const updates: { name?: string | null; description?: string | null } = {};
    if (name !== (category.name || "")) {
      updates.name = name || null;
    }
    if (description !== (category.description || "")) {
      updates.description = description || null;
    }
    if (Object.keys(updates).length > 0) {
      await updateCategory.mutateAsync({
        categoryId: category.id,
        data: updates,
      });
      setEditingName(false);
      setEditingDescription(false);
    }
  };

  const handleAddSubcategory = async () => {
    if (!newSubcategoryName.trim()) return;
    try {
      await createSubcategory.mutateAsync({
        category_id: category.id,
        name: newSubcategoryName,
        description: newSubcategoryDescription || null,
      });
      setNewSubcategoryName("");
      setNewSubcategoryDescription("");
      setAddingSubcategory(false);
    } catch (error) {
      // Error handled by mutation
    }
  };

  const handleDeleteSubcategory = async (subcategory: SubcategoryResponse) => {
    if (window.confirm(`Are you sure you want to delete "${subcategory.name}"?`)) {
      try {
        await deleteSubcategory.mutateAsync(subcategory.id);
      } catch (error) {
        // Error handled by mutation
      }
    }
  };

  return (
    <>
      <ListItem
        sx={{
          borderBottom: "1px solid",
          borderColor: "divider",
          py: 1.5,
          "&:hover": {
            backgroundColor: "action.hover",
          },
        }}
      >
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 0.75 }}>
          {/* Header with expand/collapse */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <IconButton
              size="small"
              onClick={() => setExpanded(!expanded)}
              sx={{ padding: 0.5 }}
            >
              {expanded ? (
                <ExpandLessIcon sx={{ fontSize: { xs: "1rem", sm: "1.25rem" } }} />
              ) : (
                <ExpandMoreIcon sx={{ fontSize: { xs: "1rem", sm: "1.25rem" } }} />
              )}
            </IconButton>
            <Typography variant="caption" sx={{ color: "text.secondary", fontSize: { xs: "0.65rem", sm: "0.7rem" } }}>
              {subcategories.length} {subcategories.length === 1 ? "subcategory" : "subcategories"}
            </Typography>
          </Box>

          {/* Name */}
          {editingName ? (
            <TextField
              value={name}
              onChange={(e) => setName(e.target.value)}
              onBlur={handleNameBlur}
              onKeyDown={handleNameKeyDown}
              placeholder="Category name"
              size="small"
              fullWidth
              autoFocus
              disabled={updateCategory.isPending}
              variant="standard"
              sx={{
                "& .MuiInputBase-input": {
                  fontSize: { xs: "0.875rem", sm: "1rem" },
                  py: 0.5,
                },
                "& .MuiInput-underline:before": {
                  borderBottom: "none",
                },
                "& .MuiInput-underline:hover:before": {
                  borderBottom: "none",
                },
                "& .MuiInput-underline:after": {
                  borderBottom: "none",
                },
              }}
            />
          ) : (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                cursor: "pointer",
                py: 0.5,
                "&:hover": {
                  opacity: 0.7,
                },
              }}
              onClick={() => setEditingName(true)}
            >
              <Typography
                variant="body1"
                sx={{
                  fontSize: { xs: "0.875rem", sm: "1rem" },
                  color: name ? "text.primary" : "text.secondary",
                  fontStyle: name ? "normal" : "italic",
                  fontWeight: 500,
                }}
              >
                {name || "add name"}
              </Typography>
              <EditIcon sx={{ fontSize: { xs: "0.875rem", sm: "1rem" }, color: "text.secondary" }} />
            </Box>
          )}

          {/* Description */}
          {editingDescription ? (
            <TextField
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              onBlur={handleDescriptionBlur}
              onKeyDown={handleDescriptionKeyDown}
              placeholder="Description"
              size="small"
              fullWidth
              autoFocus
              disabled={updateCategory.isPending}
              variant="standard"
              sx={{
                "& .MuiInputBase-input": {
                  fontSize: { xs: "0.75rem", sm: "0.875rem" },
                  py: 0.5,
                },
                "& .MuiInput-underline:before": {
                  borderBottom: "none",
                },
                "& .MuiInput-underline:hover:before": {
                  borderBottom: "none",
                },
                "& .MuiInput-underline:after": {
                  borderBottom: "none",
                },
              }}
            />
          ) : (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                cursor: "pointer",
                py: 0.5,
                "&:hover": {
                  opacity: 0.7,
                },
              }}
              onClick={() => setEditingDescription(true)}
            >
              <Typography
                variant="body2"
                sx={{
                  fontSize: { xs: "0.75rem", sm: "0.875rem" },
                  color: description ? "text.primary" : "text.secondary",
                  fontStyle: description ? "normal" : "italic",
                }}
              >
                {description || "add description"}
              </Typography>
              <EditIcon sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" }, color: "text.secondary" }} />
            </Box>
          )}

          {/* Actions */}
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 0.75, pt: 0.5 }}>
            <Button
              variant="text"
              startIcon={<SaveIcon />}
              disableRipple
              size="small"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleSave();
              }}
              disabled={!hasUnsavedChanges || updateCategory.isPending}
              sx={{
                textTransform: "none",
                color: "primary.main",
                px: 1,
                minWidth: "auto",
                fontSize: { xs: "0.65rem", sm: "0.75rem" },
                "& .MuiButton-startIcon": {
                  marginRight: 0.5,
                },
              }}
            >
              Save
            </Button>
            <Button
              variant="text"
              startIcon={<EditIcon />}
              disableRipple
              size="small"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onEdit(category);
              }}
              sx={{
                textTransform: "none",
                color: "text.primary",
                px: 1,
                minWidth: "auto",
                fontSize: { xs: "0.65rem", sm: "0.75rem" },
                "& .MuiButton-startIcon": {
                  marginRight: 0.5,
                },
              }}
            >
              Edit
            </Button>
            <Button
              variant="text"
              startIcon={<DeleteIcon />}
              disableRipple
              size="small"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onDelete(category);
              }}
              sx={{
                textTransform: "none",
                color: "error.main",
                px: 1,
                minWidth: "auto",
                fontSize: { xs: "0.65rem", sm: "0.75rem" },
                "& .MuiButton-startIcon": {
                  marginRight: 0.5,
                },
              }}
            >
              Delete
            </Button>
          </Box>
        </Box>
      </ListItem>

      {/* Subcategories */}
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Box>
          {subcategories.map((subcategory) => (
            <SubcategoryItem
              key={subcategory.id}
              subcategory={subcategory}
              onDelete={handleDeleteSubcategory}
            />
          ))}
          
          {/* Add new subcategory */}
          {addingSubcategory ? (
            <ListItem sx={{ pl: 6, py: 1, borderLeft: "2px solid", borderColor: "divider" }}>
              <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 1 }}>
                <TextField
                  placeholder="Subcategory name"
                  value={newSubcategoryName}
                  onChange={(e) => setNewSubcategoryName(e.target.value)}
                  size="small"
                  fullWidth
                  autoFocus
                  variant="standard"
                  sx={{
                    "& .MuiInputBase-input": {
                      fontSize: { xs: "0.75rem", sm: "0.875rem" },
                      py: 0.25,
                    },
                    "& .MuiInput-underline:before": {
                      borderBottom: "none",
                    },
                    "& .MuiInput-underline:hover:before": {
                      borderBottom: "none",
                    },
                    "& .MuiInput-underline:after": {
                      borderBottom: "none",
                    },
                  }}
                />
                <TextField
                  placeholder="Description (optional)"
                  value={newSubcategoryDescription}
                  onChange={(e) => setNewSubcategoryDescription(e.target.value)}
                  size="small"
                  fullWidth
                  variant="standard"
                  sx={{
                    "& .MuiInputBase-input": {
                      fontSize: { xs: "0.75rem", sm: "0.875rem" },
                      py: 0.25,
                    },
                    "& .MuiInput-underline:before": {
                      borderBottom: "none",
                    },
                    "& .MuiInput-underline:hover:before": {
                      borderBottom: "none",
                    },
                    "& .MuiInput-underline:after": {
                      borderBottom: "none",
                    },
                  }}
                />
                <Box sx={{ display: "flex", gap: 0.5, justifyContent: "flex-end" }}>
                  <Button
                    size="small"
                    onClick={handleAddSubcategory}
                    disabled={!newSubcategoryName.trim() || createSubcategory.isPending}
                    sx={{
                      textTransform: "none",
                      fontSize: { xs: "0.65rem", sm: "0.75rem" },
                      px: 1,
                    }}
                  >
                    Add
                  </Button>
                  <Button
                    size="small"
                    onClick={() => {
                      setAddingSubcategory(false);
                      setNewSubcategoryName("");
                      setNewSubcategoryDescription("");
                    }}
                    sx={{
                      textTransform: "none",
                      fontSize: { xs: "0.65rem", sm: "0.75rem" },
                      px: 1,
                    }}
                  >
                    Cancel
                  </Button>
                </Box>
              </Box>
            </ListItem>
          ) : (
            <ListItem sx={{ pl: 6, py: 0.5, px: 0 }}>
              <Button
                startIcon={<AddIcon />}
                onClick={() => setAddingSubcategory(true)}
                size="small"
                sx={{
                  textTransform: "none",
                  color: "primary.main",
                  fontSize: { xs: "0.65rem", sm: "0.75rem" },
                  px: 1,
                }}
              >
                Add Subcategory
              </Button>
            </ListItem>
          )}
        </Box>
      </Collapse>
    </>
  );
};
